import { CONTROL_ACTIONS, type ControlAction } from "./controlActions";

/** Only deliberately supplied metadata belongs here. Never pass field contents or full URLs. */
export interface ControlContext {
    source: string;
    field?: string;
    section?: string;
    target?: string;
    posting_id?: string;
    application_id?: string;
    resume_id?: string;
    option?: string | number | null;
    model?: string;
    plan?: string;
    billing_interval?: string;
    status?: string;
    step?: string;
    mode?: string;
    index?: number;
    count?: number;
    page_number?: number;
    has_query?: boolean;
    has_filters?: boolean;
    next_state?: boolean | string;
    options?: readonly string[];
}

/** Explicit instrumentation at the control, shared by the app and prerendered site. */
export function actionProps(
    action: ControlAction,
    context: ControlContext,
    clickAction?: ControlAction,
) {
    return {
        "data-analytics-action": action,
        "data-analytics-context": JSON.stringify(context),
        ...(clickAction ? { "data-analytics-click-action": clickAction } : {}),
    };
}

/** Context on a page, card or dialog, inherited by its explicitly named controls. */
export function scopeProps(context: Partial<ControlContext>) {
    return { "data-analytics-scope": JSON.stringify(context) };
}

export type ControlProperties = Record<string, string | number | boolean | null>;
const actions = new Set<string>(CONTROL_ACTIONS);
const contextKeys = new Set([
    "source",
    "field",
    "section",
    "target",
    "posting_id",
    "application_id",
    "resume_id",
    "option",
    "model",
    "plan",
    "billing_interval",
    "status",
    "step",
    "mode",
    "index",
    "count",
    "page_number",
    "has_query",
    "has_filters",
    "next_state",
]);

const eventKeys = new Set([
    ...contextKeys,
    "interaction",
    "control_type",
    "checked",
    "checked_before",
    "pressed_before",
    "expanded_before",
    "expanded_after",
    "blocked",
    "direction",
    "from_index",
    "to_index",
    "asked",
    "covered",
]);

/** Counts of a person's text, so only ever a number: no word can pass under these names. */
const countKeys = new Set(["asked", "covered"]);

/** Runtime allowlist also protects programmatic actions from accidental extra data. */
export function safeControlProperties(context: object): ControlProperties {
    return Object.fromEntries(
        Object.entries(context).filter(([key, value]) => {
            if (!eventKeys.has(key)) return false;
            const finite = typeof value === "number" && Number.isFinite(value);
            if (countKeys.has(key)) return finite;
            return (
                value === null || typeof value === "string" || typeof value === "boolean" || finite
            );
        }),
    );
}

/**
 * Capture phase sees the action before its handler navigates or removes a dialog.
 * Delegation also covers portals and controls rendered after startup. Names and
 * context come only from actionProps, never visible text, labels or input values.
 */
export function installControlTracking(
    capture: (name: ControlAction, properties: ControlProperties) => void,
): () => void {
    const handle = (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const control = target.closest<HTMLElement>("[data-analytics-action]");
        if (!control || control.matches(":disabled")) return;
        const name =
            event.type === "click" && control.dataset.analyticsClickAction
                ? control.dataset.analyticsClickAction
                : control.dataset.analyticsAction;
        if (!name || !actions.has(name)) return;
        const tag = control.tagName.toLowerCase();
        const input = control instanceof HTMLInputElement ? control : null;
        const field =
            input || control instanceof HTMLTextAreaElement || control instanceof HTMLSelectElement;
        if (
            event.type === "click" &&
            ((field && !control.dataset.analyticsClickAction) || tag === "form" || tag === "label")
        )
            return;
        if (event.type === "auxclick" && ((event as MouseEvent).button !== 1 || tag !== "a"))
            return;
        if (event.type === "change" && !field) return;
        if (event.type === "submit" && tag !== "form") return;
        if (event.type === "keydown") {
            const key = (event as KeyboardEvent).key;
            // Native buttons and links emit a click for keyboard activation.
            // Text fields use Enter for chip commits and search submission.
            if (
                field
                    ? key !== "Enter"
                    : control.getAttribute("role") !== "button" ||
                      !["ArrowUp", "ArrowDown"].includes(key)
            )
                return;
        }
        let context: Record<string, unknown> = {};
        try {
            const scopes: Element[] = [];
            for (let element: Element | null = control; element; element = element.parentElement) {
                if (element.hasAttribute("data-analytics-scope")) scopes.unshift(element);
            }
            for (const scope of scopes)
                Object.assign(
                    context,
                    JSON.parse(scope.getAttribute("data-analytics-scope") ?? "{}"),
                );
            const own: unknown = JSON.parse(control.dataset.analyticsContext ?? "{}");
            if (!own || typeof own !== "object" || Array.isArray(own)) return;
            context = { ...context, ...own };
        } catch {
            return;
        }
        const privateControl = Boolean(control.closest("[data-analytics-private]"));
        const properties: ControlProperties = {
            interaction: event.type,
            control_type: control.getAttribute("role") ?? input?.type ?? tag,
        };
        for (const [key, value] of Object.entries(context)) {
            if (!contextKeys.has(key) || (privateControl && key !== "source")) continue;
            if (
                value === null ||
                typeof value === "boolean" ||
                typeof value === "number" ||
                typeof value === "string"
            )
                properties[key] = value;
        }
        if (!privateControl) {
            if (
                control instanceof HTMLSelectElement &&
                Array.isArray(context.options) &&
                context.options.includes(control.value)
            )
                properties.option = control.value;
            if (event instanceof KeyboardEvent && ["ArrowUp", "ArrowDown"].includes(event.key))
                properties.direction = event.key === "ArrowUp" ? "up" : "down";
            if (input && ["checkbox", "radio"].includes(input.type))
                properties.checked = input.checked;
            for (const state of ["checked", "pressed", "expanded"] as const) {
                const before = control.getAttribute(`aria-${state}`);
                if (before === "true" || before === "false")
                    properties[`${state}_before`] = before === "true";
            }
            if (tag === "summary" && control.parentElement instanceof HTMLDetailsElement) {
                properties.expanded_before = control.parentElement.open;
                properties.expanded_after = !control.parentElement.open;
            }
        }
        if (control.getAttribute("aria-disabled") === "true") properties.blocked = true;
        capture(name as ControlAction, properties);
    };
    const types = ["click", "auxclick", "change", "submit", "keydown", "drop"];
    for (const type of types) document.addEventListener(type, handle, true);
    return () => {
        for (const type of types) document.removeEventListener(type, handle, true);
    };
}
